
<script setup>
import { ref, onMounted, computed } from "vue";
import { useRoute } from "vue-router";
import { QrCodeIcon, Bars3Icon, XMarkIcon } from "@heroicons/vue/24/outline";
//import LoadingOverlay from "./components/common/LoadingOverlay.vue";
import { useQRStore } from "./stores/qr";

// Get current route
const route = useRoute();

// Initialize QR store
const qrStore = useQRStore();

// Reactive state
const isLoading = ref(false);
const mobileMenuOpen = ref(false);

// Navigation links
const navLinks = [
  { name: "Generator", path: "/" },
  // { name: "History", path: "/history" },
  // { name: "Analytics", path: "/analytics" },
  { name: "About", path: "/about" },
];

// Computed properties
const currentYear = computed(() => new Date().getFullYear());

// Methods
const handleNavigation = (path) => {
  mobileMenuOpen.value = false;
};

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

// Lifecycle
onMounted(() => {
  // Initialize session
  qrStore.initializeSession();

  // Optional: Add any other initialization logic
  console.log("App initialized");
});

// Optional: Expose properties to template (not needed with script setup)
// defineExpose({ isLoading, navLinks })
</script>

<template>
  <div
    id="app"
    class="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-purple-800"
  >
    <!-- Navigation -->
    <nav class="bg-white/10 backdrop-blur-lg border-b border-white/20 relative">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <router-link
            to="/"
            class="flex items-center space-x-2 hover:opacity-80 transition-opacity shrink-0"
            @click="handleNavigation('/')"
          >
            <QrCodeIcon class="h-8 w-8 text-white" />
            <span class="text-xl font-bold text-white">QR Generator</span>
          </router-link>

          <!-- Desktop/tablet nav (hidden on mobile) -->
          <div class="hidden tablet:flex items-center space-x-6">
            <router-link
              v-for="link in navLinks"
              :key="link.name"
              :to="link.path"
              class="text-white/80 hover:text-white transition-colors duration-200 py-2"
              :class="{
                'text-white font-semibold border-b-2 border-white/50':
                  route.path === link.path,
              }"
              @click="handleNavigation(link.path)"
            >
              {{ link.name }}
            </router-link>
          </div>

          <!-- Mobile hamburger (visible only below tablet) -->
          <button
            type="button"
            class="tablet:hidden flex items-center justify-center min-w-[44px] min-h-[44px] -mr-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Toggle menu"
            :aria-expanded="mobileMenuOpen"
            @click="toggleMobileMenu"
          >
            <Bars3Icon v-if="!mobileMenuOpen" class="w-6 h-6" />
            <XMarkIcon v-else class="w-6 h-6" />
          </button>
        </div>
      </div>

      <!-- Mobile dropdown menu -->
      <Transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div
          v-if="mobileMenuOpen"
          class="tablet:hidden absolute top-full left-0 right-0 bg-white/10 backdrop-blur-lg border-b border-white/20 shadow-lg z-50"
        >
          <div class="max-w-7xl mx-auto px-4 py-2">
            <router-link
              v-for="link in navLinks"
              :key="link.name"
              :to="link.path"
              class="flex items-center min-h-[44px] px-4 py-3 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              :class="{
                'text-white font-semibold bg-white/10': route.path === link.path,
              }"
              @click="handleNavigation(link.path)"
            >
              {{ link.name }}
            </router-link>
          </div>
        </div>
      </Transition>

      <!-- Mobile menu overlay (tap to close) -->
      <Transition
        enter-active-class="transition-opacity duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <button
          v-if="mobileMenuOpen"
          type="button"
          class="tablet:hidden fixed inset-0 bg-black/20 z-40 cursor-default"
          aria-label="Close menu"
          @click="mobileMenuOpen = false"
        />
      </Transition>
    </nav>

    <!-- Main Content -->
    <main class="flex-1">
      <router-view v-slot="{ Component, route: currentRoute }">
        <transition name="fade" mode="out-in" appear>
          <component :is="Component" :key="currentRoute.path" />
        </transition>
      </router-view>
    </main>

    <!-- Footer -->
    <footer class="bg-white/5 backdrop-blur-lg border-t border-white/10 py-6 tablet:py-8">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center text-white/60 text-sm tablet:text-base">
          <p>
            &copy; {{ currentYear }} QR Code Generator. Built with Vue.js &
            Node.js
          </p>
          <p class="text-xs tablet:text-sm mt-2 opacity-75">
            Professional QR Code Generator with Advanced Features
          </p>
        </div>
      </div>
    </footer>

    <!-- Global Loading Overlay -->
    <!-- <LoadingOverlay v-if="isLoading" /> -->
  </div>
</template>

<style scoped>
/* Page transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Navigation active state enhancement */
.router-link-active {
  color: #ffffff; /* text-white */
  font-weight: 600; /* font-semibold */
}

</style>